import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { Currency } from '$api-client/types.gen'
import { EditServiceInfoContent } from './content'
import { EditServiceInfoControls } from './controls'

export enum Input {
  Description = 'description',
  Notes = 'notes',
  Currency = 'currency',
}

export const editServiceInfoSchema = z.object({
  [Input.Description]: z.string().max(1024, { message: 'Description must be less than 1024 characters.' }).optional(),
  [Input.Notes]: z.string().max(2048, { message: 'Internal notes must be less than 2048 characters.' }).optional(),
  [Input.Currency]: z.object({
    id: z.nativeEnum(Currency),
    value: z.nativeEnum(Currency),
  }).optional(),
})

export type EditServiceInfoSchema = z.infer<typeof editServiceInfoSchema>

type EditServiceInfoFormContextType = {
  onSubmit: (values: EditServiceInfoSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditServiceInfoFormContext = createContext<EditServiceInfoFormContextType | undefined>(undefined)

type EditServiceInfoFormProps = EditServiceInfoFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditServiceInfoSchema>,
}

export function EditServiceInfoForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditServiceInfoFormProps) {

  const context: EditServiceInfoFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditServiceInfoFormContext.Provider value={context}>
      <Form<EditServiceInfoSchema, typeof editServiceInfoSchema>
        schema={editServiceInfoSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditServiceInfoFormContext.Provider>
  )
}

export function useEditServiceInfoForm() {
  const context = useContext(EditServiceInfoFormContext)
  if (context === undefined) {
    throw new Error('useEditServiceInfoForm must be used within a EditServiceInfoFormProvider')
  }
  return context
}

// Compound components
EditServiceInfoForm.Content = EditServiceInfoContent
EditServiceInfoForm.Controls = EditServiceInfoControls
